// src/pages/DoctorsOverview/index.js
import React, { useEffect, useState } from "react";
import { Tabs, Button, Row, Drawer } from "antd";
import { ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ClinicTable from "./components/ClinicTable";
import DoctorTable from "./components/DoctorTable";
import {
  fetchClinicsList,
  fetchDoctorsList,
  fetchLocations,
} from "./DoctorsThunk";

const HELP_CONTENT = {
  doctors: {
    title: "Help Manual",
    content: (
      <div style={{ lineHeight: 1.8 }}>
        <h2
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "12px",
            marginLeft: "8px",
          }}
        >
          Add Doctor
        </h2>

        <p style={{ color: "#555", marginBottom: "16px", marginLeft: "8px" }}>
          The Add Doctor feature allows users to create and manage doctor
          records within the ERP system. Accurate doctor information helps
          support prescriptions, billing, and reporting.
        </p>

        <div
          style={{
            background: "#f5f5f5",
            padding: "14px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <strong>Steps to Add Doctor</strong>

          <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
            <li>
              Open <strong>Pharmacy → Doctor Overview</strong>
            </li>
            <li>Click the <strong>Doctors</strong> tab</li>
            <li>Click <strong>Add Doctor</strong></li>
            <li>Enter doctor details such as name, code, and contact information</li>
            <li>Link a clinic if applicable</li>
            <li>Verify all entered information</li>
            <li>Click <strong>Save</strong> to create the doctor record</li>
          </ol>
        </div>

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #eee",
            padding: "14px",
            borderRadius: "8px",
          }}
        >
          <strong>Important Notes</strong>

          <ul style={{ paddingLeft: "18px", marginTop: "10px", color: "#666" }}>
            <li>Required fields must be completed before saving.</li>
            <li>Doctor records can be edited later if needed.</li>
            <li>Each doctor can be linked to a clinic.</li>
          </ul>
        </div>
      </div>
    ),
  },
  clinics: {
    title: "Clinics Help",
    content: (
      <div style={{ lineHeight: 1.7 }}>
        <h2
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "12px",
            marginLeft: "8px",
          }}
        >
          Clinics
        </h2>

        <p style={{ color: "#555", marginBottom: "16px", marginLeft: "10px" }}>
          Manage the clinics linked to doctors for prescriptions and referral
          tracking.
        </p>

        <div
          style={{
            background: "#f5f5f5",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <strong>Clinic Management</strong>

          <ul style={{ paddingLeft: "18px", marginTop: "8px" }}>
            <li>Create clinics</li>
            <li>Edit existing clinic details</li>
            <li>Delete unused clinics</li>
            <li>Link doctors to the correct clinic</li>
          </ul>
        </div>

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #eee",
            padding: "12px",
            borderRadius: "8px",
          }}
        >
          <strong>Benefits</strong>

          <p style={{ marginTop: "8px", color: "#666" }}>
            Maintaining accurate clinic records simplifies doctor management
            and referral tracking within the ERP system.
          </p>
        </div>
      </div>
    ),
  },
};

const DoctorsOverview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("doctors");
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchClinicsList());
    dispatch(fetchDoctorsList());
    dispatch(fetchLocations());
  }, [dispatch]);

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          data-testid="doctors-overview-back-button"
        >
          Back
        </Button>
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
          onClick={() => setHelpOpen(true)}
        />
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        type="card"
      >
        <Tabs.TabPane tab="Doctors" key="doctors">
          <DoctorTable />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Clinics" key="clinics">
          <ClinicTable />
        </Tabs.TabPane>
      </Tabs>

      <Drawer
        title={HELP_CONTENT[activeTab]?.title}
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={400}
      >
        {HELP_CONTENT[activeTab]?.content}
      </Drawer>
    </div>
  );
};

export default DoctorsOverview;
